import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { ROUTES } from 'utils/constants';
import styles from './Help.module.scss';

type HowToTopic = {
  title: string;
  description: string;
  quickSteps: string[];
};

const HOW_TO_TOPICS: Record<string, HowToTopic> = {
  'organize-categories': {
    title: 'How to keep categories organized for clean reports?',
    description:
      'Categories group your activity for reporting. Define them while recording sales or expenses so your chart stays aligned with real usage.',
    quickSteps: [
      'When entering a sale or expense, choose an existing leaf category or add a category from the picker when the app allows it.',
      'Use clear names under the right parent (for example separate utilities from office expenses).',
      'Review naming periodically and consolidate duplicates when your chart grows.',
    ],
  },
  'record-transactions': {
    title: 'How to record sales and disbursements?',
    description:
      'Regular posting keeps books current. Use the Sales and Disbursements pages under Accounting (beta) to enter activity with categories and supporting details.',
    quickSteps: [
      'Open Sales for revenue entries or Disbursements for expenses.',
      'Include date, amounts, categories, and descriptions your team will recognize later.',
      'Review recent entries weekly to catch mistakes early.',
    ],
  },
  'review-report-outputs': {
    title:
      'How to Review report outputs in Accounting and validate totals before sharing?',
    description:
      'Report validation helps you catch posting errors before sending files to stakeholders. Cross-check key totals to ensure confidence in your financial output.',
    quickSteps: [
      'Open the relevant report in Accounting (Income Statement, Balance Sheet, Cash Flow, etc.).',
      'Apply the correct date range and filters before reviewing totals.',
      'Compare totals against recent transactions and only export once values are verified.',
    ],
  },
  'manage-account-settings': {
    title:
      'How to Use App Settings and Profile options to keep your account details updated?',
    description:
      'Updated account details improve security, user management, and communication. Keep profile data current so team workflows and recovery options remain reliable.',
    quickSteps: [
      'Open your Profile and verify your personal information is accurate.',
      'Use App Settings to update preferences relevant to your workflow.',
      'Change your password regularly and review account details after role or contact changes.',
    ],
  },
};

const HelpHowToTopic: React.FC = () => {
  const { topic } = useParams<{ topic: string }>();
  const data = topic ? HOW_TO_TOPICS[topic] : undefined;

  if (!data) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>How To</h1>
          <p>This help topic was not found.</p>
        </header>
        <Link to={ROUTES.HELP} className={styles.howToLink}>
          Back to User Help Center
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>{data.title}</h1>
        <p>{data.description}</p>
      </header>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>
          <Lightbulb size={20} />
          <h2>Quick Steps</h2>
        </div>
        <ol className={styles.steps}>
          {data.quickSteps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>

      <Link to={ROUTES.HELP} className={styles.howToLink}>
        Back to User Help Center
      </Link>
    </div>
  );
};

export default HelpHowToTopic;
